/**
 * The inspector. What you get when you click something on the map.
 *
 * Two pieces of *editable* state live here, and only two: the IP address an
 * operator pins to a NIC, and the link capacity they declare for a bridge.
 * Both exist because Proxmox genuinely cannot tell us the answer. Everything
 * else on this panel is read-only by construction.
 */

import { useEffect, useState } from 'react';
import type { Bridge, Guest, GuestSnapshots, HostNode, NicAttachment } from '@proxmon/shared';
import { formatBitRate } from '@proxmon/shared';
import type { MapNode } from '../map/layout.ts';
import { bytes, dayTime, mac as fmtMac, percent, relativeTime, uptime } from '../format.ts';
import { Meter } from './Meter.tsx';
import { useStore } from '../state/store.ts';

export function DetailPanel({ node }: { node: MapNode }) {
  const close = useStore((s) => s.select);

  return (
    <aside className="detail" aria-label="Details">
      <header>
        <div>
          <h2>{node.label}</h2>
          <div className="sub">{subtitleFor(node)}</div>
        </div>
        <button type="button" className="close" onClick={() => close(null)} aria-label="Close details">
          ×
        </button>
      </header>

      {node.kind === 'host' && <HostDetail host={node.data} />}
      {node.kind === 'bridge' && (
        <BridgeDetail bridge={node.data} guestCount={node.guestCount} gatewayName={node.gatewayName} />
      )}
      {node.kind === 'guest' && <GuestDetail guest={node.data} gatewayFor={node.gatewayFor} />}
    </aside>
  );
}

function subtitleFor(node: MapNode): string {
  if (node.kind === 'host') return `PVE host · ${node.data.bridges.length} bridges`;
  if (node.kind === 'bridge') return `bridge on ${node.host}`;
  return `${node.data.kind.toUpperCase()} ${node.data.vmid} · ${node.host}`;
}

// --- host -----------------------------------------------------------------

function HostDetail({ host }: { host: HostNode }) {
  return (
    <>
      <section>
        <h3>Status</h3>
        <div style={{ marginBottom: 10 }}>
          <span className={`pill ${host.online ? 'ok' : 'danger'}`}>{host.online ? 'online' : 'offline'}</span>{' '}
          {host.bridges.some((b) => b.edge) && <span className="pill edge">internet-facing</span>}
        </div>
        <Meter label="CPU" used={host.cpu} total={1} render={(u) => `${percent(u)} of ${host.cpus ?? '?'} cores`} />
        <Meter
          label="Memory"
          used={host.memUsed}
          total={host.memTotal}
          render={(u, t) => `${bytes(u)} / ${bytes(t)}`}
        />
        {host.swapTotal ? (
          <Meter label="Swap" used={host.swapUsed} total={host.swapTotal} render={(u, t) => `${bytes(u)} / ${bytes(t)}`} warnAt={0.25} dangerAt={0.6} />
        ) : null}
        <Meter
          label="Root filesystem"
          used={host.rootfsUsed}
          total={host.rootfsTotal}
          render={(u, t) => `${bytes(u)} / ${bytes(t)}`}
        />
        <dl className="kv" style={{ marginTop: 12 }}>
          <dt>Uptime</dt>
          <dd>{uptime(host.uptime)}</dd>
          <dt>Load</dt>
          <dd>{host.loadavg ? host.loadavg.map((n) => n.toFixed(2)).join('  ') : '—'}</dd>
          <dt>PVE</dt>
          <dd>{host.pveVersion ?? '—'}</dd>
          <dt>Kernel</dt>
          <dd>{host.kernel ?? '—'}</dd>
        </dl>
      </section>

      <section>
        <h3>ZFS pools</h3>
        {host.zfs.length === 0 ? (
          <p className="empty">No ZFS pools reported on this host.</p>
        ) : (
          host.zfs.map((pool) => (
            <div key={pool.name} style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                <strong style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>{pool.name}</strong>
                {pool.health && pool.health !== 'ONLINE' ? (
                  <span className="pill danger">{pool.health}</span>
                ) : (
                  <span className="pill ok">ONLINE</span>
                )}
              </div>
              <Meter
                label={`${bytes(pool.free)} free`}
                used={pool.alloc}
                total={pool.size}
                render={(u, t) => `${bytes(u)} / ${bytes(t)}`}
              />
              {pool.fragmentation !== null && (
                <div className="source-note">Fragmentation {pool.fragmentation}%</div>
              )}
            </div>
          ))
        )}
      </section>

      {host.storages.length > 0 && (
        <section>
          <h3>Storages</h3>
          {host.storages.map((storage) => (
            <Meter
              key={storage.storage}
              label={`${storage.storage} (${storage.type})`}
              used={storage.used}
              total={storage.total}
              render={(u, t) => `${bytes(u)} / ${bytes(t)}`}
            />
          ))}
        </section>
      )}

      <section>
        <h3>Bridges</h3>
        {host.bridges.map((bridge) => (
          <div key={bridge.id} className={`nic${bridge.edge ? ' edge' : ''}`}>
            <div className="nic-head">
              <span className="br">{bridge.name}</span>
              {bridge.edge && <span className="pill edge">internet</span>}
              {!bridge.active && <span className="pill idle">down</span>}
            </div>
            <dl className="kv">
              <dt>Address</dt>
              <dd>{bridge.cidr ?? '—'}</dd>
              {bridge.gateway && (
                <>
                  <dt>Gateway</dt>
                  <dd>{bridge.gateway}</dd>
                </>
              )}
              <dt>Ports</dt>
              <dd>{bridge.ports.length ? bridge.ports.join(', ') : 'none'}</dd>
            </dl>
          </div>
        ))}
      </section>

      {host.warnings.length > 0 && (
        <section>
          <h3>Warnings</h3>
          {host.warnings.map((warning) => (
            <div key={warning} className="warn-box" style={{ marginTop: 6 }}>
              {warning}
            </div>
          ))}
        </section>
      )}
    </>
  );
}

// --- bridge ---------------------------------------------------------------

function BridgeDetail({
  bridge,
  guestCount,
  gatewayName,
}: {
  bridge: Bridge;
  guestCount: number;
  gatewayName: string | null;
}) {
  return (
    <section>
      <h3>Bridge</h3>
      {gatewayName && (
        <div className="source-note" style={{ marginTop: 0, marginBottom: 10 }}>
          Reached through <strong>{gatewayName}</strong>, which has a leg on this segment and one
          further upstream — so this branch is drawn behind it on the map.
        </div>
      )}
      {bridge.edge && (
        <div className="warn-box" style={{ marginBottom: 12 }}>
          This bridge is marked internet-facing. Every guest hanging off it is treated as an exposed
          machine on the map.
        </div>
      )}
      <dl className="kv">
        <dt>Host</dt>
        <dd>{bridge.node}</dd>
        <dt>Address</dt>
        <dd>{bridge.cidr ?? '—'}</dd>
        <dt>Gateway</dt>
        <dd>{bridge.gateway ?? '—'}</dd>
        <dt>Ports</dt>
        <dd>{bridge.ports.length ? bridge.ports.join(', ') : 'none'}</dd>
        <dt>VLAN aware</dt>
        <dd>{bridge.vlanAware ? 'yes' : 'no'}</dd>
        <dt>State</dt>
        <dd>{bridge.active ? 'active' : 'inactive'}</dd>
        <dt>Guests</dt>
        <dd>{guestCount}</dd>
        <dt>Gateway</dt>
        <dd>{gatewayName ?? 'none found (hangs off the host)'}</dd>
      </dl>
      {bridge.comment && <div className="source-note">{bridge.comment}</div>}
      <BridgeTrafficCard bridge={bridge} />
    </section>
  );
}

/**
 * Throughput and saturation for one bridge.
 *
 * The capacity half is operator-supplied because Proxmox has no link speed to
 * give us — and for a bridge with no physical port there is no link speed to
 * have. Without it we still show the rate; we just cannot call it a
 * percentage of anything.
 */
function BridgeTrafficCard({ bridge }: { bridge: Bridge }) {
  const isAdmin = useStore((s) => s.session?.role === 'admin');
  const setBridgeCapacity = useStore((s) => s.setBridgeCapacity);
  const notify = useStore((s) => s.notify);

  const stored = bridge.capacity ? formatBitRate(bridge.capacity.bitsPerSec, 1) : '';
  const [draft, setDraft] = useState(stored);
  const [saving, setSaving] = useState(false);

  // Follow the server when it changes underneath us, but never overwrite
  // something half-typed.
  useEffect(() => {
    if (!saving) setDraft(bridge.capacity ? formatBitRate(bridge.capacity.bitsPerSec, 1) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bridge.capacity?.bitsPerSec]);

  const save = async (value: string | null) => {
    setSaving(true);
    try {
      await setBridgeCapacity(bridge.node, bridge.name, value);
      notify('info', value ? `Capacity for ${bridge.name} set to ${value}.` : `Cleared the capacity for ${bridge.name}.`);
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Could not save that capacity.');
    } finally {
      setSaving(false);
    }
  };

  const traffic = bridge.traffic;
  const saturation = bridge.saturation;

  return (
    <div className="traffic">
      <h4>Throughput</h4>

      {traffic ? (
        <>
          <dl className="kv">
            <dt>Into bridge</dt>
            <dd>{formatBitRate(traffic.inBytesPerSec * 8, 1)}</dd>
            <dt>Out of bridge</dt>
            <dd>{formatBitRate(traffic.outBytesPerSec * 8, 1)}</dd>
            <dt>Saturation</dt>
            <dd>
              {saturation === null ? (
                <span className="empty">set a capacity below</span>
              ) : (
                `${Math.round(saturation * 100)}% of ${formatBitRate(bridge.capacity?.bitsPerSec ?? null, 1)}`
              )}
            </dd>
          </dl>
          {bridge.capacity && (
            <Meter
              label="Peak direction"
              used={Math.max(traffic.inBytesPerSec, traffic.outBytesPerSec) * 8}
              total={bridge.capacity.bitsPerSec}
              render={(used) => formatBitRate(used, 1)}
              warnAt={0.75}
              dangerAt={0.9}
            />
          )}
          {saturation !== null && saturation > 1 && (
            <div className="warn-box" style={{ marginTop: 8 }}>
              Measured traffic exceeds the capacity entered here. Either the link is faster than{' '}
              {formatBitRate(bridge.capacity?.bitsPerSec ?? null, 1)}, or guests on this bridge are
              talking to each other and never touching the uplink.
            </div>
          )}
          <div className="source-note">
            {traffic.nicCount} guest NIC{traffic.nicCount === 1 ? '' : 's'} · {relativeTime(traffic.sampledAt)}
            {' · '}averaged over {traffic.windowSeconds.toFixed(0)}s
          </div>
        </>
      ) : (
        <div className="source-note">
          No rate yet. Throughput is the difference between two counter samples, so it appears one
          poll after a guest on this bridge starts running.
        </div>
      )}

      {/* Only guest NICs have counters. The host's own leg on the bridge is not
          a tap device, so its traffic is invisible here — worth saying out loud
          on a bridge the host itself has an address on. */}
      {bridge.cidr && (
        <div className="source-note">
          Counts guest traffic only; the host's own {bridge.cidr.split('/')[0]} traffic is not
          included.
        </div>
      )}

      {isAdmin && (
        <form
          className="ip-editor"
          onSubmit={(event) => {
            event.preventDefault();
            void save(draft.trim() || null);
          }}
        >
          <input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="link speed, e.g. 1G"
            aria-label={`Link capacity for ${bridge.name}`}
            spellCheck={false}
            autoComplete="off"
          />
          <button type="submit" className="btn" disabled={saving}>
            {saving ? '…' : 'Save'}
          </button>
          {bridge.capacity && (
            <button type="button" className="btn ghost danger" disabled={saving} onClick={() => void save(null)}>
              Clear
            </button>
          )}
        </form>
      )}
      {isAdmin && !bridge.capacity && (
        <div className="source-note">
          Proxmox does not report link speed, so saturation needs a number here. Use the speed of
          the physical port ({bridge.ports.length ? bridge.ports.join(', ') : 'none on this bridge'}
          ), or for an internal bridge whatever throughput you consider its ceiling.
        </div>
      )}
    </div>
  );
}

// --- guest ----------------------------------------------------------------

function GuestDetail({ guest, gatewayFor }: { guest: Guest; gatewayFor: string[] }) {
  return (
    <>
      <section>
        <h3>Status</h3>
        <div style={{ marginBottom: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <span className={`pill ${guest.status === 'running' ? 'ok' : 'idle'}`}>{guest.status}</span>
          {guest.edge && <span className="pill edge">internet-facing</span>}
          {gatewayFor.length > 0 && <span className="pill accent">router</span>}
          {guest.template && <span className="pill idle">template</span>}
          {guest.lock && <span className="pill danger">locked: {guest.lock}</span>}
          {guest.snapshots.severity !== 'none' && (
            <span className={`pill ${guest.snapshots.severity === 'stale' ? 'danger' : 'edge'}`}>
              {guest.snapshots.items.length} snapshot{guest.snapshots.items.length === 1 ? '' : 's'}
            </span>
          )}
        </div>

        <Meter label="CPU" used={guest.cpu} total={1} render={(u) => `${percent(u)} of ${guest.cpus ?? '?'} cores`} />
        <Meter label="Memory" used={guest.mem} total={guest.maxmem} render={(u, t) => `${bytes(u)} / ${bytes(t)}`} />
        <Meter label="Disk" used={guest.disk} total={guest.maxdisk} render={(u, t) => `${bytes(u)} / ${bytes(t)}`} />
        <DiskBreakdown guest={guest} />

        <dl className="kv" style={{ marginTop: 12 }}>
          <dt>Uptime</dt>
          <dd>{uptime(guest.uptime)}</dd>
          <dt>Host</dt>
          <dd>{guest.node}</dd>
          <dt>Type</dt>
          <dd>{guest.kind === 'qemu' ? 'QEMU virtual machine' : 'LXC container'}</dd>
        </dl>

        {gatewayFor.length > 0 && (
          <div className="warn-box" style={{ marginTop: 12 }}>
            Everything on {gatewayFor.length === 1 ? 'segment' : 'segments'}{' '}
            <strong>{gatewayFor.join(', ')}</strong> is drawn behind this machine, because it is the
            only guest bridging {gatewayFor.length === 1 ? 'that segment' : 'those segments'} to a
            more upstream one. Traffic in or out of{' '}
            {gatewayFor.length === 1 ? 'it' : 'them'} passes through here.
          </div>
        )}

        {guest.tags.length > 0 && (
          <div className="tags">
            {guest.tags.map((tag) => (
              <span key={tag} className="tag">
                {tag}
              </span>
            ))}
          </div>
        )}
      </section>

      <section>
        <h3>Network</h3>
        {guest.configStale && (
          <div className="warn-box" style={{ marginBottom: 10 }}>
            Could not read this guest's configuration on the last poll — the interfaces below may be
            out of date.
          </div>
        )}
        {guest.nics.length === 0 ? (
          <p className="empty">No network interfaces configured.</p>
        ) : (
          guest.nics.map((nic) => <NicCard key={nic.device} nic={nic} />)
        )}
      </section>

      <SnapshotSection snapshots={guest.snapshots} />
    </>
  );
}

/**
 * Snapshots, and why they are worth a panel of their own.
 *
 * A Proxmox snapshot is not a backup: it is a copy-on-write delta that the
 * guest keeps writing into for as long as it exists, so an old one costs
 * growing disk and slowing writes on the live machine — and it is sitting on
 * the same disks that would fail. The panel says which ones exist, how old,
 * and leaves the removing to Proxmox, because Proxmon never writes.
 */
function SnapshotSection({ snapshots }: { snapshots: GuestSnapshots }) {
  if (snapshots.disabled) return null;

  return (
    <section>
      <h3>Snapshots</h3>

      {snapshots.unreadable ? (
        <div className="warn-box">
          Could not read this guest's snapshots on the last poll. This is not the same as having
          none — if it persists, the PVE token needs <strong>VM.Audit</strong> on the guest.
        </div>
      ) : snapshots.items.length === 0 ? (
        <p className="empty">None. Nothing is accumulating a delta on this guest.</p>
      ) : (
        <>
          <div className={snapshots.severity === 'stale' ? 'warn-box danger' : 'warn-box'}>
            {snapshots.severity === 'stale' ? (
              <>
                The oldest of these was taken {relativeTime(snapshots.oldestTakenAt)} and is still
                here. A snapshot that old has been growing a delta on the live disks ever since;
                removing it is usually the fix.
              </>
            ) : (
              <>
                A snapshot is not a backup. It lives on the same disks as the guest and grows for as
                long as it exists, so remove it once the change it was taken for has stuck.
              </>
            )}
          </div>

          <div className="snapshot-list">
            {snapshots.items.map((snap) => (
              <div className="snapshot" key={snap.name}>
                <div className="snapshot-head">
                  <span className="name">{snap.name}</span>
                  {snap.withRam && <span className="pill idle">with RAM</span>}
                </div>
                <div className="snapshot-when">
                  {snap.takenAt === null ? 'no date recorded' : `${dayTime(snap.takenAt)} · ${relativeTime(snap.takenAt)}`}
                </div>
                {snap.description && <div className="snapshot-desc">{snap.description}</div>}
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

/**
 * What the guest is actually provisioned.
 *
 * The meter above measures the boot volume, because that is the only one
 * Proxmox reports usage for. This is the rest of the truth: every disk the
 * config declares, and what they come to together.
 */
function DiskBreakdown({ guest }: { guest: Guest }) {
  if (guest.disks.length === 0) return null;

  const unsized = guest.disks.filter((disk) => disk.size === null).length;

  return (
    <div className="disk-list">
      {guest.disks.map((disk) => (
        <div className="disk" key={disk.device}>
          <span className="dev">{disk.device}</span>
          <span>{disk.size === null ? 'size not stated' : bytes(disk.size)}</span>
          <span className="store">{disk.storage ?? 'passthrough'}</span>
        </div>
      ))}

      {guest.disks.length > 1 && (
        <div className="disk" style={{ borderTop: '1px solid var(--line-soft)', paddingTop: 4 }}>
          <span className="dev">total</span>
          <span>{bytes(guest.diskTotal)}</span>
        </div>
      )}

      {unsized > 0 && (
        <div className="source-note">
          {unsized === 1 ? 'One disk declares' : `${unsized} disks declare`} no size, so{' '}
          {unsized === 1 ? 'it is' : 'they are'} left out of the total.
        </div>
      )}
    </div>
  );
}

const SOURCE_LABEL: Record<string, string> = {
  manual: 'set by you',
  'lxc-config': 'from the container config',
  'qemu-agent': 'reported by the guest agent',
};

function NicCard({ nic }: { nic: NicAttachment }) {
  const isAdmin = useStore((s) => s.session?.role === 'admin');
  const setIpOverride = useStore((s) => s.setIpOverride);
  const notify = useStore((s) => s.notify);

  const [draft, setDraft] = useState(nic.ip?.source === 'manual' ? nic.ip.address : '');
  const [saving, setSaving] = useState(false);

  // Adopt the server's value when it changes underneath us (another tab, or a
  // fresh poll), but never clobber a half-typed entry.
  useEffect(() => {
    if (!saving) setDraft(nic.ip?.source === 'manual' ? nic.ip.address : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nic.ip?.address, nic.ip?.source]);

  const save = async (value: string | null) => {
    if (!nic.mac) return;
    setSaving(true);
    try {
      await setIpOverride(nic.mac, value);
      notify('info', value ? `Pinned ${value} to ${nic.device}.` : `Cleared the address for ${nic.device}.`);
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Could not save that address.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`nic${nic.edge ? ' edge' : ''}`}>
      <div className="nic-head">
        <span className="dev">{nic.device}</span>
        <span className="br">{nic.bridge}</span>
        {nic.edge && <span className="pill edge">internet</span>}
        {nic.vlanTag !== null && <span className="tag">VLAN {nic.vlanTag}</span>}
        {nic.firewall && <span className="tag">fw</span>}
      </div>

      <dl className="kv">
        <dt>MAC</dt>
        <dd>{fmtMac(nic.mac)}</dd>
        <dt>Address</dt>
        <dd>{nic.ip ? nic.ip.address : <span className="empty">unknown</span>}</dd>
        {nic.model && (
          <>
            <dt>Model</dt>
            <dd>{nic.model}</dd>
          </>
        )}
      </dl>

      {nic.ip && (
        <div className="source-note">
          {SOURCE_LABEL[nic.ip.source] ?? nic.ip.source}
          {nic.ip.source === 'qemu-agent' && nic.ip.observedAt ? ` · ${relativeTime(nic.ip.observedAt)}` : ''}
        </div>
      )}

      {/* Proxmox cannot see the address of a bridged VM that configures its own
          networking, so on the exposed bridges we let the operator say what it
          is. Keyed by MAC, which survives clones and migrations. */}
      {isAdmin && nic.mac && (
        <form
          className="ip-editor"
          onSubmit={(event) => {
            event.preventDefault();
            void save(draft.trim() || null);
          }}
        >
          <input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={nic.ip && nic.ip.source !== 'manual' ? nic.ip.address : 'set IP address'}
            aria-label={`IP address for ${nic.device}`}
            spellCheck={false}
            autoComplete="off"
          />
          <button type="submit" className="btn" disabled={saving}>
            {saving ? '…' : 'Save'}
          </button>
          {nic.ip?.source === 'manual' && (
            <button type="button" className="btn ghost danger" disabled={saving} onClick={() => void save(null)}>
              Clear
            </button>
          )}
        </form>
      )}
    </div>
  );
}
